import {
    AutomationRule,
    AutomationWorker,
    AutomationWorkerId,
    cloneAutomationWorkers,
} from './worker-automation.types';
import { AUTOMATION_WORKER_DEFINITIONS } from './worker-automation.constants';
import {
    normalizeAutomationRuleForEdit,
    normalizeAutomationRuleForPersist,
} from './worker-automation.utils';
import { WorkflowConfigMap, WorkflowStatusConfig } from '../worker-configuration.types';
import { isRetiredWorkflowOption } from '../worker-configuration.permissions';

/** Option key owned by the custom rules editor (not rendered as a generic OptionField). */
export const AUTOMATION_RULES_OPTION_KEY = 'rules';

const WORKER_KEY_TO_ID = Object.fromEntries(
    Object.entries(AUTOMATION_WORKER_DEFINITIONS).map(([id, definition]) => [
        definition.key,
        id as AutomationWorkerId,
    ]),
) as Record<string, AutomationWorkerId>;

export function createEmptyAutomationWorkers(): Record<AutomationWorkerId, AutomationWorker> {
    return cloneAutomationWorkers(
        Object.fromEntries(
            Object.entries(AUTOMATION_WORKER_DEFINITIONS).map(([id, definition]) => [
                id,
                {
                    ...definition,
                    rules: [],
                },
            ]),
        ) as Record<AutomationWorkerId, AutomationWorker>,
    );
}

function isAutomationWorkerConfigKey(key: string): boolean {
    return key in WORKER_KEY_TO_ID;
}

/** True when a workflowStatus / config key is owned by the denial/rejection automation UI. */
export function isAutomationWorkerWorkflowStatus(workflowStatus: string | null | undefined): boolean {
    return Boolean(workflowStatus?.trim() && isAutomationWorkerConfigKey(workflowStatus.trim()));
}

export function getAutomationWorkerEnabledOptionKey(
    workerId: AutomationWorkerId,
): 'workDenial' | 'workRejection' {
    return workerId === 'denial' ? 'workDenial' : 'workRejection';
}

function sortRulesByPriority(rules: AutomationRule[]): AutomationRule[] {
    if (!rules.some(rule => typeof rule.priority === 'number')) {
        return rules;
    }

    return [...rules].sort((left, right) => (left.priority ?? 0) - (right.priority ?? 0));
}

function parseAutomationRules(value: WorkflowStatusConfig): AutomationRule[] {
    const rules = (value as Record<string, unknown>).rules;
    if (!Array.isArray(rules)) {
        return [];
    }

    return sortRulesByPriority(rules.map((rule: AutomationRule) => normalizeAutomationRuleForEdit(rule)));
}

/** Keeps option fields for OptionField rendering; rules stay on the automation worker. */
function toAutomationOptionConfig(
    workerId: AutomationWorkerId,
    workerKey: string,
    value: WorkflowStatusConfig,
): WorkflowStatusConfig {
    const next: WorkflowStatusConfig = {};

    for (const [key, optionValue] of Object.entries(value)) {
        if (key === AUTOMATION_RULES_OPTION_KEY || isRetiredWorkflowOption(workerKey, key)) {
            continue;
        }
        next[key] = optionValue;
    }

    const enabledKey = getAutomationWorkerEnabledOptionKey(workerId);
    if (!(enabledKey in next) && typeof next.active === 'boolean') {
        next[enabledKey] = next.active;
        delete next.active;
    } else if ('active' in next) {
        delete next.active;
    }

    return next;
}

export function splitWorkflowConfiguration(config?: WorkflowConfigMap): {
    automationWorkers: Record<AutomationWorkerId, AutomationWorker>;
    workflowConfig: WorkflowConfigMap;
} {
    const workflowConfig: WorkflowConfigMap = {};
    const automationWorkers = createEmptyAutomationWorkers();

    if (!config) {
        return { workflowConfig, automationWorkers };
    }

    for (const [key, value] of Object.entries(config)) {
        if (isAutomationWorkerConfigKey(key)) {
            const workerId = WORKER_KEY_TO_ID[key];
            const base = automationWorkers[workerId];
            automationWorkers[workerId] = {
                ...base,
                rules: parseAutomationRules(value),
            };
            workflowConfig[key] = toAutomationOptionConfig(workerId, key, value);
            continue;
        }

        workflowConfig[key] = value;
    }

    return { workflowConfig, automationWorkers };
}

export function mergeWorkflowConfiguration(
    workflowConfig: WorkflowConfigMap,
    automationWorkers: Record<AutomationWorkerId, AutomationWorker>,
): WorkflowConfigMap {
    // Plain-clone so MobX observables cannot leak into the PUT/POST payload or diff.
    const merged: WorkflowConfigMap = JSON.parse(JSON.stringify(workflowConfig ?? {}));
    const plainWorkers = JSON.parse(JSON.stringify(automationWorkers)) as Record<
        AutomationWorkerId,
        AutomationWorker
    >;

    for (const worker of Object.values(plainWorkers)) {
        const optionConfig = { ...(merged[worker.key] ?? {}) };
        delete optionConfig[AUTOMATION_RULES_OPTION_KEY];
        // Grouping lives on each rule now; a worker-level `group` must never be written back.
        for (const key of Object.keys(optionConfig)) {
            if (isRetiredWorkflowOption(worker.key, key)) {
                delete optionConfig[key];
            }
        }

        const rules = (Array.isArray(worker.rules) ? worker.rules : []).map((rule, index) => ({
            ...normalizeAutomationRuleForPersist(rule),
            priority: index + 1,
        }));

        merged[worker.key] = {
            ...optionConfig,
            [AUTOMATION_RULES_OPTION_KEY]: rules,
        } as unknown as WorkflowStatusConfig;
    }

    return merged;
}

export function readAutomationWorkerEnabled(
    workflowConfig: WorkflowConfigMap,
    worker: Pick<AutomationWorker, 'id' | 'key'>,
    fallback = true,
): boolean {
    const value = workflowConfig[worker.key]?.[getAutomationWorkerEnabledOptionKey(worker.id)];
    return typeof value === 'boolean' ? value : fallback;
}

export function isAutomationRulesOptionKey(key: string | null | undefined): boolean {
    return key === AUTOMATION_RULES_OPTION_KEY;
}
